// Toast service using singleton pattern to bridge between service calls and ToastContext
// The ToastContext initializes this singleton, allowing the service to be called from anywhere

interface ToastOptions {
  message: string;
  color?: 'success' | 'danger' | 'warning' | 'primary' | 'secondary' | 'tertiary';
  duration?: number;
  position?: 'top' | 'bottom' | 'middle';
  buttons?: Array<{
    text: string;
    role?: string;
    handler?: () => void;
  }>;
}

interface ToastServiceOptions {
  color?: string;
  position?: 'top' | 'bottom' | 'middle';
  duration?: number;
  cssClass?: string;
}

const DEFAULT_DURATION = 3000; // 3 seconds

// Singleton instance that gets initialized by ToastContext
class ToastService {
  private showToastFn: ((options: ToastOptions) => void) | null = null;

  initialize(showToastFn: (options: ToastOptions) => void) {
    this.showToastFn = showToastFn;
  }

  private show(options: ToastOptions) {
    if (!this.showToastFn) {
      console.error('ToastService not initialized. Make sure ToastProvider is wrapping your app.');
      return;
    }
    this.showToastFn(options);
  }

  // Helper to strip HTML tags from message
  private stripHtml(html: string): string {
    return html.replace(/<[^>]*>/g, '');
  }

  showSuccess(
    message: string,
    additionalMessages?: string[],
    additionalOptions?: ToastServiceOptions
  ) {
    if (additionalMessages && additionalMessages.length) {
      message = message + '\n\n' + additionalMessages.join('\n');
    }

    this.show({
      message: this.stripHtml(message),
      color: (additionalOptions?.color as any) || 'success',
      duration: additionalOptions?.duration || DEFAULT_DURATION,
      position: additionalOptions?.position || 'top'
    });
  }

  showInfo(
    message: string,
    additionalMessages?: string[],
    additionalOptions?: ToastServiceOptions
  ) {
    if (additionalMessages && additionalMessages.length) {
      message = message + '\n\n' + additionalMessages.join('\n');
    }

    this.show({
      message: this.stripHtml(message),
      color: (additionalOptions?.color as any) || 'primary',
      duration: additionalOptions?.duration || DEFAULT_DURATION,
      position: additionalOptions?.position || 'top'
    });
  }

  showError(
    messages: string | string[],
    additionalMessages?: string[],
    additionalOptions?: ToastServiceOptions,
    mustDismiss?: boolean
  ) {
    let duration = mustDismiss ? 0 : DEFAULT_DURATION * 3;

    let messageArray = messages ? (Array.isArray(messages) ? messages : [messages]) : ['No error messages returned'];

    // Process error messages to clean them up
    const processedMessages = messageArray.map((e: string) => {
      let message = '';
      const arr1 = e.split('\n\t*');
      arr1.forEach((m: string) => {
        const arr2 = m.split(' [');
        for (let i = 0; i < arr2.length; i++) {
          let val = arr2[i].replace('\n\n', '');
          val = val.trim();
          if (!val.includes('http status:') && !val.includes('errors occurred')) {
            val = val.substring(0, 1).toUpperCase() + val.substring(1);
            message += `${i > 0 ? '[' : ''}${val}\n`;
          }
        }
      });
      return message;
    });

    // Add additional messages if provided
    let finalMessage = processedMessages.join('\n');
    if (additionalMessages && additionalMessages.length) {
      finalMessage = finalMessage + '\n\n' + additionalMessages.join('\n');
    }

    this.show({
      message: this.stripHtml(finalMessage),
      color: (additionalOptions?.color as any) || 'danger',
      duration: additionalOptions?.duration || duration,
      position: additionalOptions?.position || 'top'
    });
  }

  toastWithRoute(
    message: string,
    route: string,
    onNavigate: (route: string) => void,
    additionalMessages?: string[],
    additionalOptions?: ToastServiceOptions
  ) {
    if (additionalMessages && additionalMessages.length) {
      message = message + '\n\n' + additionalMessages.join('\n');
    }

    this.show({
      message: this.stripHtml(message),
      color: (additionalOptions?.color as any) || 'primary',
      duration: additionalOptions?.duration || DEFAULT_DURATION * 3,
      position: additionalOptions?.position || 'top',
      buttons: [
        {
          text: 'Open',
          handler: () => onNavigate(route)
        },
        {
          text: 'X',
          role: 'cancel'
        }
      ]
    });
  }

  toastWithButton(
    message: string,
    buttonText: string,
    route: string,
    onNavigate: (route: string) => void,
    additionalMessages?: string[],
    additionalOptions?: ToastServiceOptions
  ) {
    if (additionalMessages && additionalMessages.length) {
      message = message + '\n\n' + additionalMessages.join('\n');
    }

    this.show({
      message: this.stripHtml(message),
      color: (additionalOptions?.color as any) || 'success',
      duration: additionalOptions?.duration || DEFAULT_DURATION * 3,
      position: additionalOptions?.position || 'bottom',
      buttons: [
        {
          text: buttonText,
          handler: () => onNavigate(route)
        }
      ]
    });
  }
}

// Export singleton instance
const toastServiceInstance = new ToastService();
export default toastServiceInstance;

// Export individual functions for convenience (matching old API)
export async function showSuccess(
  message: string,
  additionalMessages?: string[],
  additionalOptions?: ToastServiceOptions
): Promise<void> {
  toastServiceInstance.showSuccess(message, additionalMessages, additionalOptions);
}

export async function showInfo(
  message: string,
  additionalMessages?: string[],
  additionalOptions?: ToastServiceOptions
): Promise<void> {
  toastServiceInstance.showInfo(message, additionalMessages, additionalOptions);
}

export async function showError(
  messages: string | string[],
  additionalMessages?: string[],
  additionalOptions?: ToastServiceOptions,
  mustDismiss?: boolean
): Promise<void> {
  toastServiceInstance.showError(messages, additionalMessages, additionalOptions, mustDismiss);
}

export async function toastWithRoute(
  message: string,
  route: string,
  onNavigate: (route: string) => void,
  additionalMessages?: string[],
  additionalOptions?: ToastServiceOptions
): Promise<void> {
  toastServiceInstance.toastWithRoute(message, route, onNavigate, additionalMessages, additionalOptions);
}

export async function toastWithButton(
  message: string,
  buttonText: string,
  route: string,
  onNavigate: (route: string) => void,
  additionalMessages?: string[],
  additionalOptions?: ToastServiceOptions
): Promise<void> {
  toastServiceInstance.toastWithButton(message, buttonText, route, onNavigate, additionalMessages, additionalOptions);
}

// Team-specific toast functions
export async function showTeamNameSaved(): Promise<void> {
  showSuccess('Team name saved successfully!', [], { position: 'top' });
}

export async function showTeamNameSaveFailed(errorMessage?: string): Promise<void> {
  showError('Failed to save team name', errorMessage ? [errorMessage] : ['Please try again'], { position: 'top' });
}

export async function showInvitationsSentSuccess(count: number): Promise<void> {
  showSuccess(`Invitation${count > 1 ? 's' : ''} sent successfully!`, [], { position: 'top' });
}

export async function showInvitationsPartialSuccess(successCount: number, errorCount: number): Promise<void> {
  showError(`${successCount} invitation${successCount > 1 ? 's' : ''} sent, ${errorCount} failed`, [], { position: 'top' });
}

export async function showInvitationsFailed(count: number): Promise<void> {
  showError(`Failed to send invitation${count > 1 ? 's' : ''}`, [], { position: 'top' });
}

export async function showMemberSuspended(memberName: string): Promise<void> {
  showSuccess(`${memberName} has been suspended`, [], { position: 'top' });
}

export async function showMemberSuspendFailed(errorMessage?: string): Promise<void> {
  showError('Failed to suspend team member', errorMessage ? [errorMessage] : ['Please try again'], { position: 'top' });
}

export async function showMemberActivated(memberName: string): Promise<void> {
  showSuccess(`${memberName} has been activated`, [], { position: 'top' });
}

export async function showMemberActivateFailed(errorMessage?: string): Promise<void> {
  showError('Failed to activate team member', errorMessage ? [errorMessage] : ['Please try again'], { position: 'top' });
}

export async function showMemberRemoved(memberName: string): Promise<void> {
  showSuccess(`${memberName} has been removed`, [], { position: 'top' });
}

export async function showMemberRemoveFailed(errorMessage?: string): Promise<void> {
  showError('Failed to remove team member', errorMessage ? [errorMessage] : ['Please try again'], { position: 'top' });
}

// Data refresh toast functions
export async function showDataRefreshed(): Promise<void> {
  showSuccess('Data refreshed', [], { position: 'top' });
}

export async function showDataRefreshFailed(errorMessage?: string): Promise<void> {
  showError('Failed to refresh data', errorMessage ? [errorMessage] : ['Please try again'], { position: 'top' });
}

// Convenience export
export const toastService = {
  showSuccess,
  showInfo,
  showError,
  toastWithRoute,
  toastWithButton,
  showTeamNameSaved,
  showTeamNameSaveFailed,
  showInvitationsSentSuccess,
  showInvitationsPartialSuccess,
  showInvitationsFailed,
  showMemberSuspended,
  showMemberSuspendFailed,
  showMemberActivated,
  showMemberActivateFailed,
  showMemberRemoved,
  showMemberRemoveFailed,
  showDataRefreshed,
  showDataRefreshFailed,
};
